import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  ShoppingCart, Plus, Filter, Search, Download, 
  DollarSign, Clock, CheckCircle, XCircle, 
  AlertTriangle, ArrowUpRight, ArrowDownRight, RefreshCw,
  Eye, Edit, MoreVertical, Calendar, MapPin,
  Truck, Globe, Shield, FileText, PieChart,
  Activity, CreditCard, Receipt, Thermometer,
  Droplets, Zap, Box, Palette, Layers, Cube, Archive,
  Target, TrendingDown, Users, Package, Settings, BarChart3,
  UserCheck, UserX, Clock4, AlertCircle, CheckSquare, Square, MessageSquare
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, ScatterChart, Scatter
} from 'recharts';

const PurchaseRequests = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRequest, setSelectedRequest] = useState('all');
  const [timeRange, setTimeRange] = useState('30d');
  const { isDarkMode } = useChainsTheme();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock data for purchase requests
  const requestMetrics = [
    { 
      name: 'Total Requests', 
      value: '156', 
      change: '+12%', 
      trend: 'up', 
      icon: ShoppingCart, 
      color: 'blue',
      pending: 23,
      approved: 98,
      rejected: 35
    },
    { 
      name: 'Total Value', 
      value: 'SAR 2.8M', 
      change: '+8.5%', 
      trend: 'up', 
      icon: DollarSign, 
      color: 'green',
      pending: 450000,
      approved: 1850000,
      rejected: 500000
    },
    { 
      name: 'Approval Rate', 
      value: '73.1%', 
      change: '+2.1%', 
      trend: 'up', 
      icon: CheckCircle, 
      color: 'emerald',
      pending: 0,
      approved: 98,
      rejected: 35
    },
    { 
      name: 'Avg Processing Time', 
      value: '3.2 days', 
      change: '-15.3%', 
      trend: 'down', 
      icon: Clock, 
      color: 'cyan',
      pending: 0,
      approved: 0,
      rejected: 0
    },
    { 
      name: 'Budget Utilization', 
      value: '68.5%', 
      change: '+5.2%', 
      trend: 'up', 
      icon: Target, 
      color: 'orange',
      pending: 0,
      approved: 0,
      rejected: 0
    },
    { 
      name: 'Urgent Requests', 
      value: '8', 
      change: '-2', 
      trend: 'down', 
      icon: AlertTriangle, 
      color: 'red',
      pending: 8,
      approved: 0,
      rejected: 0
    }
  ];

  const purchaseRequests = [
    { 
      id: 'PR-2024-001', 
      title: 'IT Equipment for New Office', 
      requester: 'Ahmed Al-Rashid', 
      department: 'IT', 
      category: 'IT Equipment',
      amount: 125000.00, 
      status: 'pending', 
      priority: 'high', 
      createdAt: '2024-01-15',
      expectedDelivery: '2024-01-25',
      items: 15,
      description: 'Laptops, monitors, and networking equipment for new office setup',
      budget: 150000,
      approver: 'Sarah Johnson',
      attachments: 3,
      comments: 5
    },
    { 
      id: 'PR-2024-002', 
      title: 'Office Supplies Monthly Order', 
      requester: 'Mike Chen', 
      department: 'Administration', 
      category: 'Office Supplies',
      amount: 8500.00, 
      status: 'approved', 
      priority: 'medium', 
      createdAt: '2024-01-14',
      expectedDelivery: '2024-01-22',
      items: 25,
      description: 'Monthly office supplies including paper, pens, and stationery',
      budget: 10000,
      approver: 'Lisa Rodriguez',
      attachments: 1,
      comments: 2
    },
    { 
      id: 'PR-2024-003', 
      title: 'Logistics Services Q1', 
      requester: 'David Kim', 
      department: 'Operations', 
      category: 'Logistics',
      amount: 150000.00, 
      status: 'in_review', 
      priority: 'high', 
      createdAt: '2024-01-13',
      expectedDelivery: '2024-01-30',
      items: 8,
      description: 'Logistics and transportation services for Q1 operations',
      budget: 180000,
      approver: 'John Smith',
      attachments: 5,
      comments: 8
    },
    { 
      id: 'PR-2024-004', 
      title: 'Express Delivery Services', 
      requester: 'Lisa Rodriguez', 
      department: 'Sales', 
      category: 'Transportation',
      amount: 12000.00, 
      status: 'approved', 
      priority: 'urgent', 
      createdAt: '2024-01-12',
      expectedDelivery: '2024-01-20',
      items: 12,
      description: 'Express delivery services for urgent customer orders',
      budget: 15000,
      approver: 'Mike Chen',
      attachments: 2,
      comments: 3
    },
    { 
      id: 'PR-2024-005', 
      title: 'Raw Materials for Production', 
      requester: 'Sarah Johnson', 
      department: 'Production', 
      category: 'Raw Materials',
      amount: 85000.00, 
      status: 'rejected', 
      priority: 'medium', 
      createdAt: '2024-01-11',
      expectedDelivery: '2024-01-28',
      items: 18,
      description: 'Raw materials needed for Q1 production schedule',
      budget: 100000,
      approver: 'Ahmed Al-Rashid',
      attachments: 4,
      comments: 6
    }
  ];

  const requestTrendsData = [
    { month: 'Jan', requests: 45, approved: 32, rejected: 8, pending: 5, amount: 1250000 },
    { month: 'Feb', requests: 52, approved: 38, rejected: 10, pending: 4, amount: 1380000 },
    { month: 'Mar', requests: 58, approved: 42, rejected: 12, pending: 4, amount: 1520000 },
    { month: 'Apr', requests: 65, approved: 48, rejected: 13, pending: 4, amount: 1680000 },
    { month: 'May', requests: 72, approved: 55, rejected: 14, pending: 3, amount: 1820000 },
    { month: 'Jun', requests: 78, approved: 62, rejected: 15, pending: 1, amount: 1950000 },
  ];

  const categoryData = [
    { name: 'IT Equipment', value: 35, color: '#3B82F6' },
    { name: 'Office Supplies', value: 25, color: '#10B981' },
    { name: 'Logistics', value: 20, color: '#F59E0B' },
    { name: 'Transportation', value: 15, color: '#EF4444' },
    { name: 'Raw Materials', value: 5, color: '#8B5CF6' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400';
      case 'approved': return 'text-green-400';
      case 'rejected': return 'text-red-400';
      case 'in_review': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20';
      case 'approved': return 'bg-green-500/20';
      case 'rejected': return 'bg-red-500/20';
      case 'in_review': return 'bg-blue-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500/20';
      case 'high': return 'bg-orange-500/20';
      case 'medium': return 'bg-yellow-500/20';
      case 'low': return 'bg-green-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const RequestMetricCard = ({ metric }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${metric.color}-500/20`}>
          <metric.icon className={`w-6 h-6 text-${metric.color}-500`} />
        </div>
        <div className={`flex items-center text-sm ${
          metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
        }`}>
          {metric.trend === 'up' ? (
            <ArrowUpRight className="w-4 h-4 mr-1" />
          ) : (
            <ArrowDownRight className="w-4 h-4 mr-1" />
          )}
          {metric.change}
        </div>
      </div>
      
      <div className="mb-4">
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.value}</p>
        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{metric.name}</p>
      </div>

      {metric.pending > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Pending</span>
            <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.pending}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Approved</span>
            <span className={`text-xs font-medium text-green-400`}>{metric.approved}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Rejected</span>
            <span className={`text-xs font-medium text-red-400`}>{metric.rejected}</span>
          </div>
        </div>
      )}
    </motion.div>
  );

  const RequestCard = ({ request }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.id}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(request.status)} ${getStatusColor(request.status)}`}>
              {request.status.replace('_', ' ')}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBg(request.priority)} ${getPriorityColor(request.priority)}`}>
              {request.priority}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{request.title}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Requester</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.requester}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Department</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.department}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Category</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.category}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Items</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.items}</p>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Budget</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {request.budget.toLocaleString()}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Expected Delivery</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.expectedDelivery}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Approver</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.approver}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Created</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.createdAt}</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="text-right">
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {request.amount.toLocaleString()}</p>
          <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Total Amount</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 text-xs">
            <FileText className={`w-3 h-3 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
            <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>{request.attachments}</span>
          </div>
          <div className="flex items-center space-x-1 text-xs">
            <MessageSquare className={`w-3 h-3 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
            <span className={isDarkMode ? 'text-white/60' : 'text-gray-600'}>{request.comments}</span>
          </div>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
                  <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Purchase Requests</h1>
        <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-700'}`}>Request management, approval workflows, and budget tracking</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <Download className="w-5 h-5 mr-2 inline" />
            Export
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            New Request
          </motion.button>
        </div>
      </motion.div>

      {/* Request Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {requestMetrics.map((metric, index) => (
          <motion.div
            key={metric.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <RequestMetricCard metric={metric} />
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Trends */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Request Trends</h3>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Requests</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Approved</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={requestTrendsData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#f0f0f0'} />
              <XAxis dataKey="month" stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <YAxis stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
              <Bar dataKey="requests" fill="#3B82F6" fillOpacity={0.3} />
              <Line type="monotone" dataKey="approved" stroke="#10B981" strokeWidth={3} />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Category Distribution */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Requests by Category</h3>
            <button className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}>View All</button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {categoryData.map((category, index) => (
              <div key={category.name} className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>{category.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-1 p-1 backdrop-blur-sm rounded-xl border border-white/20">
        {[
          { id: 'overview', label: 'Overview', count: requestMetrics.length },
          { id: 'pending', label: 'Pending', count: purchaseRequests.filter(r => r.status === 'pending').length },
          { id: 'approved', label: 'Approved', count: purchaseRequests.filter(r => r.status === 'approved').length },
          { id: 'rejected', label: 'Rejected', count: purchaseRequests.filter(r => r.status === 'rejected').length }
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <span>{tab.label}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
              activeTab === tab.id ? 'bg-white/20' : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
            }`}>
              {tab.count}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Content based on active tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'pending' && (
          <motion.div
            key="pending"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {purchaseRequests.filter(r => r.status === 'pending').map((request, index) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <RequestCard request={request} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'approved' && (
          <motion.div
            key="approved"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {purchaseRequests.filter(r => r.status === 'approved').map((request, index) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <RequestCard request={request} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'rejected' && (
          <motion.div
            key="rejected"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {purchaseRequests.filter(r => r.status === 'rejected').map((request, index) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <RequestCard request={request} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Requests</h3>
              {purchaseRequests.slice(0, 2).map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <RequestCard request={request} />
                </motion.div>
              ))}
            </div>
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Urgent Requests</h3>
              {purchaseRequests.filter(r => r.priority === 'urgent').slice(0, 2).map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <RequestCard request={request} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PurchaseRequests; 